import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/QuoteForm.css";

const API_URL = process.env.REACT_APP_API_URL;

const getTodayLocal = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const localDate = new Date(now.getTime() - offset * 60 * 1000);
  return localDate.toISOString().split("T")[0];
};

const formatDateIt = (value) => {
  if (!value) return "-";

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("it-IT");
};

const formatTipologia = (value) => {
  if (!value) return "-";

  return String(value)
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const QuoteForm = () => {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [categorie, setCategorie] = useState([]);
  const [categoriaSelezionata, setCategoriaSelezionata] = useState(null);

  const [giocatori, setGiocatori] = useState([]);
  const [idGiocatore, setIdGiocatore] = useState(null);

  const [stagioni, setStagioni] = useState([]);
  const [idStagione, setIdStagione] = useState(null);

  const [scadenze, setScadenze] = useState([]);
  const [idScadenzaRate, setIdScadenzaRate] = useState(null);

  const [metodoPagamento, setMetodoPagamento] = useState("");
  const [dataPagamento, setDataPagamento] = useState(getTodayLocal);

  const [importoPagamento, setImportoPagamento] = useState("");
  const [aggiungiImporto, setAggiungiImporto] = useState(true);

  const [quoteGiocatore, setQuoteGiocatore] = useState([]);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [certificazioneDisponibile, setCertificazioneDisponibile] = useState(null);
  const [sendingCertificazioneEmail, setSendingCertificazioneEmail] = useState(false);
  const [openingCertificazionePdf, setOpeningCertificazionePdf] = useState(false);

  const metodiPagamento = ["Contanti", "Bonifico", "POS", "Satispay"];

  const safeText = async (res) => {
    try {
      return await res.text();
    } catch {
      return "";
    }
  };

  const formatMoney = (v) => {
    const n = Number(v);
    if (Number.isNaN(n)) return "-";
    return `${n.toFixed(2)} €`;
  };

  const parseAmount = (v) => {
    const n = Number(String(v ?? "").replace(",", "."));
    return Number.isNaN(n) ? 0 : n;
  };

  const resetPagamentoFields = () => {
    setIdScadenzaRate(null);
    setMetodoPagamento("");
    setImportoPagamento("");
    setAggiungiImporto(true);
  };

  const resetCertificazione = () => {
    setCertificazioneDisponibile(null);
  };

  const scaricaRicevuta = async (idQuota) => {
    const res = await authFetch(`${API_URL}/api/admin/quote/${idQuota}/ricevuta`);
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(txt || "Errore download ricevuta");
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `ricevuta_quota_${idQuota}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
  };

  const getImportoResiduo = (q) => {
    const dovuto = parseAmount(q?.importoDovuto);
    const pagato = parseAmount(q?.importoPagato);
    return dovuto - pagato;
  };

  useEffect(() => {
    const load = async () => {
      setError("");

      try {
        const resCat = await authFetch(`${API_URL}/api/admin/categorie`);
        if (!resCat.ok) {
          throw new Error(`Errore /categorie ${resCat.status}: ${await safeText(resCat)}`);
        }
        const data = await resCat.json();
        setCategorie(Array.isArray(data) ? data : []);
      } catch (e) {
        setError(String(e.message || e));
      }

      try {
        const resSt = await authFetch(`${API_URL}/api/admin/stagioni`);
        if (!resSt.ok) {
          throw new Error(`Errore /stagioni ${resSt.status}: ${await safeText(resSt)}`);
        }
        const data = await resSt.json();
        setStagioni(Array.isArray(data) ? data : []);
      } catch (e) {
        setError((prev) => prev || String(e.message || e));
      }
    };

    load();
  }, [authFetch]);

  useEffect(() => {
    const loadGiocatori = async () => {
      setError("");

      if (categoriaSelezionata == null) {
        setGiocatori([]);
        setIdGiocatore(null);
        return;
      }

      const url =
        `${API_URL}/api/admin/giocatori` +
        `?idCategoria=${categoriaSelezionata}` +
        `&stato=ATTIVO` +
        `&page=0&size=200&sort=cognome&dir=ASC`;

      const res = await authFetch(url);
      if (!res.ok) {
        throw new Error(`Errore /giocatori ${res.status}: ${await safeText(res)}`);
      }

      const page = await res.json();
      const list = Array.isArray(page?.content) ? page.content : [];
      setGiocatori(list);
      setIdGiocatore(null);
    };

    loadGiocatori().catch((e) => setError(String(e.message || e)));
  }, [authFetch, categoriaSelezionata]);

  useEffect(() => {
    const loadScadenze = async () => {
      setError("");

      if (idStagione == null) {
        setScadenze([]);
        setIdScadenzaRate(null);
        return;
      }

      const params = new URLSearchParams();
      params.append("idStagione", String(idStagione));
      params.append("stato", "ATTIVO");

      const url = `${API_URL}/api/admin/scadenze?${params.toString()}`;
      const res = await authFetch(url);

      if (!res.ok) {
        throw new Error(`Errore /scadenze ${res.status}: ${await safeText(res)}`);
      }

      const data = await res.json();
      setScadenze(Array.isArray(data) ? data : []);
      setIdScadenzaRate(null);
    };

    loadScadenze().catch((e) => setError(String(e.message || e)));
  }, [authFetch, idStagione]);

  useEffect(() => {
    const loadQuote = async () => {
      setError("");

      if (idGiocatore == null || idStagione == null) {
        setQuoteGiocatore([]);
        return;
      }

      const url = `${API_URL}/api/admin/quote/giocatore/${idGiocatore}?idStagione=${idStagione}`;
      const res = await authFetch(url);

      if (!res.ok) {
        throw new Error(`Errore /quote ${res.status}: ${await safeText(res)}`);
      }

      const data = await res.json();
      setQuoteGiocatore(Array.isArray(data) ? data : []);
    };

    loadQuote().catch((e) => setError(String(e.message || e)));
  }, [authFetch, idGiocatore, idStagione]);

  const quoteInizialiRegistrate = useMemo(() => {
    return (quoteGiocatore || []).filter((q) => {
      return q?.tipologia === "PRE_ISCRIZIONE" || q?.tipologia === "ISCRIZIONE";
    });
  }, [quoteGiocatore]);

  const quotaInizialePresente = useMemo(() => {
    return quoteInizialiRegistrate.length > 0;
  }, [quoteInizialiRegistrate]);

  const tipoQuotaInizialePresente = useMemo(() => {
    return quoteInizialiRegistrate[0]?.tipologia || null;
  }, [quoteInizialiRegistrate]);

  const esistePreTraScadenze = useMemo(() => {
    return (scadenze || []).some((s) => s?.tipologia === "PRE_ISCRIZIONE");
  }, [scadenze]);

  const esisteIscrizioneTraScadenze = useMemo(() => {
    return (scadenze || []).some((s) => s?.tipologia === "ISCRIZIONE");
  }, [scadenze]);

  const pianoStagionaleDeterminato = useMemo(() => {
    if (tipoQuotaInizialePresente === "PRE_ISCRIZIONE" || tipoQuotaInizialePresente === "ISCRIZIONE") {
      return true;
    }

    if (esistePreTraScadenze && esisteIscrizioneTraScadenze) {
      return false;
    }

    return true;
  }, [tipoQuotaInizialePresente, esistePreTraScadenze, esisteIscrizioneTraScadenze]);

  const quotaEsistenteSelezionata = useMemo(() => {
    if (idScadenzaRate == null) return null;

    return (
      (quoteGiocatore || []).find(
        (q) => String(q.idScadenzaRate ?? q.idScadenza ?? q.idScadenzaRata) === String(idScadenzaRate)
      ) || null
    );
  }, [quoteGiocatore, idScadenzaRate]);

  const scadenzaSelezionata = useMemo(() => {
    if (idScadenzaRate == null) return null;
    return (
      (scadenze || []).find(
        (s) => String(s.idScadenzaRate ?? s.id) === String(idScadenzaRate)
      ) || null
    );
  }, [scadenze, idScadenzaRate]);

  const scadenzeFiltrate = useMemo(() => {
    const elenco = Array.isArray(scadenze) ? [...scadenze] : [];

    return elenco.filter((s) => {
      if (!s) return false;

      if (quotaInizialePresente) {
        if (s?.tipologia === "PRE_ISCRIZIONE" || s?.tipologia === "ISCRIZIONE") {
          if (String(s.tipologia) !== String(tipoQuotaInizialePresente)) {
            return false;
          }
        }
      }

      const quotaAssociata = (quoteGiocatore || []).find(
        (q) =>
          String(q.idScadenzaRate ?? q.idScadenza ?? q.idScadenzaRata) ===
          String(s.idScadenzaRate ?? s.id)
      );

      if (!quotaAssociata) return true;

      const dovuto = parseAmount(quotaAssociata?.importoDovuto);
      const pagato = parseAmount(quotaAssociata?.importoPagato);
      const residuo = dovuto - pagato;

      return residuo > 0;
    });
  }, [scadenze, quoteGiocatore, quotaInizialePresente, tipoQuotaInizialePresente]);

  const totaleDovutoStagionale = useMemo(() => {
    return (quoteGiocatore || []).reduce(
      (sum, q) => sum + parseAmount(q.importoDovuto),
      0
    );
  }, [quoteGiocatore]);

  const totalePagato = useMemo(() => {
    return (quoteGiocatore || []).reduce((sum, q) => sum + parseAmount(q.importoPagato), 0);
  }, [quoteGiocatore]);

  const residuoTotale = useMemo(() => {
    return totaleDovutoStagionale - totalePagato;
  }, [totaleDovutoStagionale, totalePagato]);

  useEffect(() => {
    const stagioneCompletamentePagata =
      idGiocatore != null &&
      idStagione != null &&
      quoteGiocatore.length > 0 &&
      residuoTotale <= 0;

    if (stagioneCompletamentePagata) {
      setCertificazioneDisponibile({
        idGiocatore,
        idStagione
      });
    } else {
      setCertificazioneDisponibile(null);
    }
  }, [idGiocatore, idStagione, quoteGiocatore, residuoTotale]);

  useEffect(() => {
    if (idScadenzaRate == null) return;
    if (!scadenzaSelezionata) return;

    const isScadenzaIniziale =
      scadenzaSelezionata?.tipologia === "PRE_ISCRIZIONE" ||
      scadenzaSelezionata?.tipologia === "ISCRIZIONE";

    if (quotaInizialePresente && isScadenzaIniziale) {
      if (String(scadenzaSelezionata.tipologia) !== String(tipoQuotaInizialePresente)) {
        setIdScadenzaRate(null);
        setImportoPagamento("");
      }
    }
  }, [idScadenzaRate, scadenzaSelezionata, quotaInizialePresente, tipoQuotaInizialePresente]);

  useEffect(() => {
    if (idScadenzaRate == null) {
      setImportoPagamento("");
      return;
    }

    const scad = (scadenze || []).find(
      (s) => String(s.idScadenzaRate ?? s.id) === String(idScadenzaRate)
    );
    const prev = scad?.importoPrevisto;

    if (prev !== undefined && prev !== null && prev !== "") {
      setImportoPagamento(Number(prev).toFixed(2));
    }
  }, [idScadenzaRate, scadenze]);

  const refreshQuote = async () => {
    if (idGiocatore == null || idStagione == null) return;

    const url = `${API_URL}/api/admin/quote/giocatore/${idGiocatore}?idStagione=${idStagione}`;
    const res = await authFetch(url);

    if (res.ok) {
      const data = await res.json();
      setQuoteGiocatore(Array.isArray(data) ? data : []);
    }
  };

  const handleApriCertificazionePdf = async () => {
    setMessage("");
    setError("");

    if (!certificazioneDisponibile?.idGiocatore || !certificazioneDisponibile?.idStagione) {
      setError("Certificazione non disponibile.");
      return;
    }

    setOpeningCertificazionePdf(true);

    try {
      const res = await authFetch(`${API_URL}/api/admin/quote/certificazione`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(certificazioneDisponibile)
      });

      if (!res.ok) {
        throw new Error((await safeText(res)) || "Errore durante la generazione del PDF");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setOpeningCertificazionePdf(false);
    }
  };

  const handleInviaCertificazioneEmail = async () => {
    setMessage("");
    setError("");

    if (!certificazioneDisponibile?.idGiocatore || !certificazioneDisponibile?.idStagione) {
      setError("Certificazione non disponibile.");
      return;
    }

    setSendingCertificazioneEmail(true);

    try {
      const res = await authFetch(`${API_URL}/api/admin/quote/certificazione/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(certificazioneDisponibile)
      });

      if (!res.ok) {
        throw new Error((await safeText(res)) || "Errore durante l'invio della certificazione");
      }

      setMessage("Certificazione inviata correttamente via email.");
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setSendingCertificazioneEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (categoriaSelezionata == null) return setError("Seleziona la categoria.");
    if (idGiocatore == null) return setError("Seleziona il giocatore.");
    if (idStagione == null) return setError("Seleziona la stagione.");
    if (idScadenzaRate == null) return setError("Seleziona una scadenza.");
    if (!metodoPagamento) return setError("Seleziona un metodo di pagamento.");

    const importoNum = Number(String(importoPagamento).replace(",", "."));
    if (importoPagamento === "" || Number.isNaN(importoNum) || importoNum < 0) {
      return setError("Inserisci un importo valido (>= 0).");
    }

    const scadenzaIniziale =
      scadenzaSelezionata?.tipologia === "PRE_ISCRIZIONE" ||
      scadenzaSelezionata?.tipologia === "ISCRIZIONE";

    if (
      scadenzaSelezionata &&
      quotaInizialePresente &&
      scadenzaIniziale &&
      String(scadenzaSelezionata.tipologia) !== String(tipoQuotaInizialePresente)
    ) {
      return setError(
        "Per questo giocatore è già presente la quota iniziale alternativa per la stagione selezionata."
      );
    }

    if (quotaEsistenteSelezionata) {
      const residuo = getImportoResiduo(quotaEsistenteSelezionata);

      if (aggiungiImporto && importoNum > residuo) {
        return setError(`L'importo inserito supera il residuo della quota (${formatMoney(residuo)}).`);
      }

      if (!aggiungiImporto) {
        const dovuto = parseAmount(quotaEsistenteSelezionata.importoDovuto);
        if (importoNum > dovuto) {
          return setError(`Il totale pagato non può superare l'importo dovuto (${formatMoney(dovuto)}).`);
        }
      }
    }

    try {
      if (quotaEsistenteSelezionata?.id) {
        const payloadPut = {
          importoPagato: importoNum,
          aggiungi: Boolean(aggiungiImporto),
          dataPagamento: dataPagamento || null,
          metodoPagamento: metodoPagamento || null
        };

        const res = await authFetch(
          `${API_URL}/api/admin/quote/${quotaEsistenteSelezionata.id}/pagamento`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payloadPut)
          }
        );

        if (!res.ok) {
          throw new Error((await safeText(res)) || "Errore durante l'aggiornamento del pagamento");
        }

        setMessage("Pagamento aggiornato con successo!");
      } else {
        const payloadPost = {
          idGiocatore,
          idScadenzaRate,
          importoPagato: importoNum,
          dataUltimoPagamento: dataPagamento || null,
          metodoUltimoPagamento: metodoPagamento || null
        };

        const res = await authFetch(`${API_URL}/api/admin/quote`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payloadPost)
        });

        if (!res.ok) {
          throw new Error((await safeText(res)) || "Errore durante il salvataggio");
        }

        setMessage("Pagamento registrato con successo!");
      }

      resetPagamentoFields();
      await refreshQuote();
    } catch (err) {
      setError(String(err.message || err));
    }
  };

  const handleSaldaQuota = () => {
    setMessage("");
    setError("");

    if (!quotaEsistenteSelezionata) return;

    const residuo = getImportoResiduo(quotaEsistenteSelezionata);

    if (residuo <= 0) {
      setError("Questa quota è già saldata.");
      return;
    }

    setImportoPagamento(residuo.toFixed(2));
    setAggiungiImporto(true);
    setMessage("Importo impostato per saldo quota.");
  };

  const handleSaldoTotaleBackend = async () => {
    setMessage("");
    setError("");

    if (categoriaSelezionata == null) return setError("Seleziona la categoria.");
    if (idGiocatore == null) return setError("Seleziona il giocatore.");
    if (idStagione == null) return setError("Seleziona la stagione.");
    if (!metodoPagamento) return setError("Seleziona un metodo di pagamento.");

    if (residuoTotale <= 0) {
      return setError("Non ci sono importi da saldare.");
    }

    try {
      const payload = {
        idGiocatore,
        idStagione,
        importo: Number(residuoTotale.toFixed(2)),
        dataPagamento: dataPagamento || null,
        metodoPagamento: metodoPagamento || null
      };

      const res = await authFetch(`${API_URL}/api/admin/quote/saldo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error((await safeText(res)) || "Errore durante il saldo totale");
      }

      setMessage("Saldo totale stagione registrato con successo!");
      resetPagamentoFields();
      await refreshQuote();
    } catch (err) {
      setError(String(err.message || err));
    }
  };

  const categoriaOptions = categorie.map((cat) => ({
    value: String(cat.id),
    label: cat.nome
  }));

  const giocatoreOptions = giocatori.map((g) => ({
    value: String(g.id),
    label: `${g.cognome} ${g.nome}`
  }));

  const stagioneOptions = stagioni.map((s) => ({
    value: String(s.id),
    label: s.nome
  }));

  const scadenzaOptions = scadenzeFiltrate.map((s) => ({
    value: String(s.idScadenzaRate ?? s.id),
    label: `${formatTipologia(s.tipologia)} - scadenza ${formatDateIt(s.dataScadenza)} - ${formatMoney(
      s.importoPrevisto
    )}`
  }));

  const selectedCategoria =
    categoriaOptions.find((opt) => opt.value === String(categoriaSelezionata)) || null;

  const selectedGiocatore =
    giocatoreOptions.find((opt) => opt.value === String(idGiocatore)) || null;

  const selectedStagione =
    stagioneOptions.find((opt) => opt.value === String(idStagione)) || null;

  const selectedScadenza =
    scadenzaOptions.find((opt) => opt.value === String(idScadenzaRate)) || null;

  const quoteOrdinate = useMemo(() => {
    return [...(quoteGiocatore || [])].sort((a, b) => {
      const ordineA = Number(a?.ordine ?? 9999);
      const ordineB = Number(b?.ordine ?? 9999);
      return ordineA - ordineB;
    });
  }, [quoteGiocatore]);

  return (
    <form onSubmit={handleSubmit} className="quote-form-container" translate="no">
      <h2 className="quote-form-title">Registra Pagamento Quota</h2>

      <div className="quote-sezione">
        <h3 className="quote-sezione-titolo">Contesto pagamento</h3>

        <div className="quote-form-grid">
          <div className="quote-form-group">
            <label className="quote-label">Categoria</label>
            <Select
              classNamePrefix="quote-react-select"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              options={categoriaOptions}
              value={selectedCategoria}
              onChange={(selectedOption) => {
                const value = selectedOption ? Number(selectedOption.value) : null;
                setCategoriaSelezionata(value);
                setGiocatori([]);
                setIdGiocatore(null);
                setIdStagione(null);
                setScadenze([]);
                setQuoteGiocatore([]);
                resetPagamentoFields();
                resetCertificazione();
                setMessage("");
                setError("");
              }}
              placeholder="Seleziona categoria"
              isClearable
              isSearchable
              noOptionsMessage={() => "Nessuna categoria trovata"}
            />
          </div>

          <div className="quote-form-group">
            <label className="quote-label">Giocatore</label>
            <Select
              classNamePrefix="quote-react-select"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              options={giocatoreOptions}
              value={selectedGiocatore}
              onChange={(selectedOption) => {
                const value = selectedOption ? Number(selectedOption.value) : null;
                setIdGiocatore(value);
                setIdStagione(null);
                setScadenze([]);
                setQuoteGiocatore([]);
                resetPagamentoFields();
                resetCertificazione();
                setMessage("");
                setError("");
              }}
              placeholder="Seleziona giocatore"
              isClearable
              isSearchable
              isDisabled={categoriaSelezionata == null}
              noOptionsMessage={() => "Nessun giocatore trovato"}
            />
          </div>

          <div className="quote-form-group">
            <label className="quote-label">Stagione</label>
            <Select
              classNamePrefix="quote-react-select"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              options={stagioneOptions}
              value={selectedStagione}
              onChange={(selectedOption) => {
                const value = selectedOption ? Number(selectedOption.value) : null;
                setIdStagione(value);
                setScadenze([]);
                setQuoteGiocatore([]);
                resetPagamentoFields();
                resetCertificazione();
                setMessage("");
                setError("");
              }}
              placeholder="Seleziona stagione"
              isClearable
              isSearchable
              isDisabled={idGiocatore == null}
              noOptionsMessage={() => "Nessuna stagione trovata"}
            />
          </div>

          <div className="quote-form-group">
            <label className="quote-label">Scadenza</label>
            <Select
              classNamePrefix="quote-react-select"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              options={scadenzaOptions}
              value={selectedScadenza}
              onChange={(selectedOption) =>
                setIdScadenzaRate(selectedOption ? Number(selectedOption.value) : null)
              }
              placeholder="Seleziona scadenza"
              isClearable
              isSearchable
              isDisabled={idStagione == null}
              noOptionsMessage={() => "Nessuna scadenza trovata"}
            />
          </div>
        </div>

        {quotaInizialePresente && (
          <div className="quote-info-box">
            <b>Quota iniziale già presente:</b>{" "}
            {tipoQuotaInizialePresente === "PRE_ISCRIZIONE" ? "Pre-iscrizione" : "Iscrizione"}.
            Non è possibile registrare l'alternativa per la stessa stagione.
          </div>
        )}

        {quotaEsistenteSelezionata && (
          <div className="quote-info-box">
            <b>Quota già presente:</b> stato <b>{quotaEsistenteSelezionata.statoPagamento}</b> — dovuto{" "}
            <b>{formatMoney(quotaEsistenteSelezionata.importoDovuto)}</b> — pagato{" "}
            <b>{formatMoney(quotaEsistenteSelezionata.importoPagato)}</b> — residuo{" "}
            <b>{formatMoney(getImportoResiduo(quotaEsistenteSelezionata))}</b>
          </div>
        )}

        {idStagione != null && pianoStagionaleDeterminato && (
          <div className="quote-info-box">
            <b>Situazione stagione:</b> dovuto <b>{formatMoney(totaleDovutoStagionale)}</b> — pagato{" "}
            <b>{formatMoney(totalePagato)}</b> — residuo <b>{formatMoney(residuoTotale)}</b>
          </div>
        )}

        {quotaEsistenteSelezionata && (
          <div className="quote-action-row">
            <button type="button" className="quote-mini-button" onClick={handleSaldaQuota}>
              Salda quota
            </button>
          </div>
        )}

        {idStagione != null && pianoStagionaleDeterminato && (
          <div className="quote-action-row">
            <button type="button" className="quote-mini-button" onClick={handleSaldoTotaleBackend}>
              Salda totale stagione
            </button>
          </div>
        )}
      </div>

      <div className="quote-sezione">
        <h3 className="quote-sezione-titolo">Dati pagamento</h3>

        <div className="quote-form-grid">
          <div className="quote-form-group">
            <label className="quote-label">Importo pagamento</label>
            <input
              className="quote-input"
              type="number"
              step="0.01"
              min="0"
              value={importoPagamento}
              onChange={(e) => setImportoPagamento(e.target.value)}
              required
            />
          </div>

          <div className="quote-form-group">
            <label className="quote-label">Metodo Pagamento</label>
            <select
              className="quote-select"
              value={metodoPagamento}
              onChange={(e) => setMetodoPagamento(e.target.value)}
              required
            >
              <option value="">Seleziona metodo</option>
              {metodiPagamento.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="quote-form-group">
            <label className="quote-label">Data pagamento</label>
            <input
              className="quote-input"
              type="date"
              value={dataPagamento}
              onChange={(e) => setDataPagamento(e.target.value)}
              required
            />
          </div>

          {quotaEsistenteSelezionata && (
            <div className="quote-form-group quote-full-width">
              <label className="quote-checkbox-label">
                <input
                  type="checkbox"
                  checked={aggiungiImporto}
                  onChange={(e) => setAggiungiImporto(e.target.checked)}
                />
                <span>Aggiungi l’importo al già pagato (se disattivo, imposto il totale pagato)</span>
              </label>
            </div>
          )}
        </div>
      </div>

      {certificazioneDisponibile && (
        <div className="quote-sezione">
          <h3 className="quote-sezione-titolo">Certificazione saldo stagione</h3>

          <div className="quote-info-box">
            La stagione risulta interamente saldata. Ora puoi inviare la certificazione via email oppure aprire il PDF per la stampa.
          </div>

          <div className="quote-action-row">
            <button
              type="button"
              className="quote-mini-button"
              onClick={handleInviaCertificazioneEmail}
              disabled={sendingCertificazioneEmail || openingCertificazionePdf}
            >
              {sendingCertificazioneEmail ? "Invio email..." : "Invia certificazione via email"}
            </button>

            <button
              type="button"
              className="quote-mini-button"
              onClick={handleApriCertificazionePdf}
              disabled={openingCertificazionePdf || sendingCertificazioneEmail}
            >
              {openingCertificazionePdf ? "Apertura PDF..." : "Apri / stampa PDF"}
            </button>
          </div>
        </div>
      )}

      {message && <div className="quote-success-message">{message}</div>}
      {error && <div className="quote-error-message">{error}</div>}

      <div className="quote-button-row">
        <button className="quote-button" type="submit">
          {quotaEsistenteSelezionata ? "Aggiorna Pagamento" : "Registra Pagamento"}
        </button>
        <button className="quote-button" type="button" onClick={() => navigate("/gestionale")}>
          Torna al Gestionale
        </button>
      </div>

      {quoteGiocatore.length > 0 && (
        <div className="quote-tabella-container">
          <h3 className="quote-tabella-title">Quote / Pagamenti registrati</h3>

          <table className="quote-tabella">
            <thead>
              <tr>
                <th>Tipologia</th>
                <th>Dovuto</th>
                <th>Pagato</th>
                <th>Residuo</th>
                <th>Scadenza</th>
                <th>Ultimo pagamento</th>
                <th>Stato</th>
                <th>Metodo</th>
                <th>Ricevuta</th>
              </tr>
            </thead>
            <tbody>
              {quoteOrdinate.map((q) => (
                <tr key={q.id}>
                  <td>{formatTipologia(q.tipologia)}</td>
                  <td>{formatMoney(q.importoDovuto)}</td>
                  <td>{formatMoney(q.importoPagato)}</td>
                  <td>{formatMoney(getImportoResiduo(q))}</td>
                  <td>{formatDateIt(q.dataScadenza)}</td>
                  <td>{formatDateIt(q.dataUltimoPagamento)}</td>
                  <td>{q.statoPagamento || "-"}</td>
                  <td>{q.metodoUltimoPagamento || "-"}</td>
                  <td>
                    <button
                      type="button"
                      className="quote-mini-button"
                      onClick={() => scaricaRicevuta(q.id).catch((e) => setError(e.message))}
                    >
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </form>
  );
};

export default QuoteForm;